import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageResolution } from '../types';
import { processImage } from '../utils/processImage';
import { useWorkerMemoryManagement } from './useWorkerMemoryManagement';

const WORKER_URL = '/workers/openCVWorker.js';
const MAX_CONSECUTIVE_ERRORS = 3;

/**
 * Configuration for OpenCV worker behavior
 */
type OpenCVWorkerConfig = {
  /**
   * The current image resolution in pixels
   */
  imageResolution: ImageResolution;
  /**
   * Whether to skip color correction
   */
  skip?: boolean;
};

/**
 * OpenCV worker message event data structure
 */
type OpenCVWorkerMessageData = {
  action: 'ready' | 'result' | 'memoryWarning' | 'error';
  dataURL?: string;
  finalClipLimit?: number;
  error?: boolean;
  message?: string;
};

/**
 * Return value from the useOpenCVWorker hook
 */
type UseOpenCVWorkerReturn = {
  /**
   * Whether the worker is currently ready to process images
   */
  isWorkerReady: boolean;

  /**
   * Function to process an image with the current worker
   */
  processImage: (image: HTMLImageElement) => void;
};

export const useOpenCVWorker = (
  config: OpenCVWorkerConfig,
  onProcessingStart: () => void,
  onProcessingComplete: (
    finalClipLimit: number,
    error: boolean,
    processedImage: HTMLImageElement | null
  ) => void,
  onProcessingError: (message: string) => void
): UseOpenCVWorkerReturn => {
  const workerRef = useRef<Worker | null>(null);
  const [isSpawning, setIsSpawning] = useState(false);
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  const processingErrorCount = useRef(0);
  const spawnErrorCount = useRef(0);

  const { currentMemoryThreshold, maxMemoryReached, increaseMemory } =
    useWorkerMemoryManagement(config.imageResolution);

  const releaseWorker = useCallback((message?: string) => {
    if (workerRef.current) {
      console.debug(message ?? '🗑️ Releasing OpenCV worker');
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  const spawnWorker = useCallback(() => {
    if (config.skip) {
      console.debug('Skipping color correction, returning null image');
      onProcessingComplete(0, false, null);
      return;
    }

    if (spawnErrorCount.current >= MAX_CONSECUTIVE_ERRORS) {
      console.debug('❌ Too many consecutive spawn errors, cancelling spawn');
      onProcessingError('Error spawning OpenCV worker');
      return;
    }

    if (isSpawning) {
      console.debug('Worker already spawning, skipping spawn');
      return;
    }

    setIsSpawning(true);
    setIsWorkerReady(false);

    try {
      if (workerRef.current) {
        console.debug('Terminating existing worker');
        workerRef.current.terminate();
        workerRef.current = null;
      }

      // Create new worker
      console.debug(`⚙️ Creating new OpenCV worker...`);
      const newWorker = new Worker(WORKER_URL);
      console.debug('📡 Worker created, waiting for OpenCV.js to load...');

      // Add handlers for worker messages
      newWorker.onmessage = (e: MessageEvent<OpenCVWorkerMessageData>) => {
        const { action, dataURL, finalClipLimit, error, message } = e.data;

        switch (action) {
          // update the worker ready state
          case 'ready': {
            console.debug('✅ OpenCV worker ready!');
            // reset the spawn error count
            spawnErrorCount.current = 0;
            setIsWorkerReady(true);
            break;
          }
          // update the final image and clip limit
          case 'result': {
            console.debug(
              '🖼️ OpenCV worker result received, final clip limit:',
              finalClipLimit
            );
            // reset the processing error count
            processingErrorCount.current = 0;
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = dataURL!;
            img.onload = () => {
              onProcessingComplete(finalClipLimit || 0, error || false, img);
            };
            releaseWorker('🗑️ Releasing worker after successful processing');
            break;
          }
          // handle memory warning
          case 'memoryWarning': {
            console.debug('⚠️ Memory warning received');

            if (maxMemoryReached) {
              console.debug('❌ Maximum memory threshold reached');
              releaseWorker(
                '🗑️ Terminating worker due to reaching its memory limit'
              );
              onProcessingError(
                'Memory thresholds exhausted, unable to process image'
              );
              return;
            }

            increaseMemory();
            break;
          }
          // handle error
          case 'error': {
            console.error('❌ Color correction worker error:', message);
            if (workerRef.current) {
              releaseWorker('🗑️ Terminating worker due to error');
            }
            processingErrorCount.current = processingErrorCount.current + 1;
            if (processingErrorCount.current >= MAX_CONSECUTIVE_ERRORS) {
              console.debug('❌ Too many consecutive errors, will not retry');
              onProcessingError(message || 'Unknown error');
              return;
            }
            console.debug(
              `🔄 Retrying with new worker (${processingErrorCount.current}/${MAX_CONSECUTIVE_ERRORS})`
            );
            spawnWorker();
            break;
          }
        }
      };

      workerRef.current = newWorker;
    } catch (error) {
      console.error('Error spawning OpenCV worker', error);
      onProcessingError('Error spawning OpenCV worker');
      spawnErrorCount.current = spawnErrorCount.current + 1;
    } finally {
      setIsSpawning(false);
    }
  }, [
    config.skip,
    increaseMemory,
    isSpawning,
    maxMemoryReached,
    onProcessingComplete,
    onProcessingError,
    releaseWorker,
  ]);

  // invoke the provided processing start callback, then return the result of the image processing
  const processImageWithWorker = useCallback(
    (image: HTMLImageElement) => {
      if (!isWorkerReady) {
        console.debug(
          `⚠️ processImageWithWorker called, but worker ${workerRef.current} is not ready`
        );
      }

      if (!workerRef.current) {
        console.error('⚠️ Color correction worker not initialized.');
        spawnWorker();

        return null;
      }

      console.debug(
        `🖼️ Processing image with ${currentMemoryThreshold}MB memory limit`
      );

      onProcessingStart();

      return processImage(image, workerRef.current, currentMemoryThreshold);
    },
    [currentMemoryThreshold, isWorkerReady, onProcessingStart, spawnWorker]
  );

  useEffect(() => {
    spawnWorker();
    return () => {
      releaseWorker();
    };
  }, [spawnWorker, releaseWorker]);

  return {
    processImage: processImageWithWorker,
    isWorkerReady,
  };
};
